/* eslint-disable unicorn/prevent-abbreviations */ // as EnvId is a known Ops abbreviation.
/* eslint-disable unicorn/filename-case */ // TODO: long as eslint is not configured for kebab-case
import { getSubAppConfig } from "./frontend-subapp-config";

/**
 * If a sitename contains an EnvId suffix return it (e.g. au for my-site-au) or else return null.
 *
 * (Exported for jest: ~@access private~)
 *
 * @param {string} sitename - e.g. ukdemo in ukdemo.cirrusplatform.com .
 * @return {string} - envId e.g. ca for test-education-ca or null (e.g for ukdemo)
 */
export const sitenameSuffixToEnvId = (sitename) => {
  const suffixMatch = sitename.match(
    /.+?-((au|ca|sg|eu|euprem)(temp)?|at|next|rc|preview|eu[0-5]|local)$/
  );

  if (suffixMatch !== null) {
    // eslint-disable-next-line prefer-destructuring
    return suffixMatch[1];
  }

  return null;
};

/**
 * Convert a customer's sitename to the EnvId (e.g. au for prod-au) where its data should be hosted.
 *
 * NOTE: Updates required here (and its unit tests) if a customer is not hosted on prod-eu (or moves).
 * NOTE: First update unit test __tests__/frontend-config.spec.es6 before making any changes!!!
 *
 * (Exported for jest: ~@access private~)
 * @throws an error if called without a sitename parameter.
 *
 * @param {string} sitename - e.g. ukdemo in ukdemo.cirrusplatform.com .
 * @return {string} - envId e.g. au for prod-au .
 */
export const sitenameToEnvId = (sitename) => {
  if (sitename === undefined || sitename === null) {
    throw new Error("sitenameToEnvId: sitename missing or null");
  }

  // EU Premium (prod-euprem) first - as highest risk:
  if (
    false ||
    sitename === "pat" ||
    sitename === "ati" ||
    sitename === "cai" ||
    sitename === "cai-mockexams" ||
    sitename === "test-cai" ||
    sitename === "icas"
  ) {
    return "euprem";
  }

  // Australia (prod-au):
  if (
    false ||
    sitename.startsWith("ausdemo") ||
    sitename.startsWith("charlessturt") ||
    sitename.startsWith("cpaaustralia") ||
    sitename.startsWith("csu") ||
    sitename.startsWith("deakin")
  ) {
    return "au";
  }

  // Canada (prod-ca):
  if (false || sitename.startsWith("bccohp") ||
      sitename.startsWith("mcmaster")) {
    return "ca";
  }

  // Singapore (prod-sg):
  if (
    false ||
    sitename === "cva" ||
    sitename === "isca" ||
    sitename === "mindef" ||
    sitename === "pentest" ||
    sitename === "pentest-customer" ||
    sitename === "sac" ||
    sitename === "zionext"
  ) {
    return "sg";
  }

  const overrideEnvId = sitenameSuffixToEnvId(sitename);

  if (overrideEnvId !== null) {
    return overrideEnvId;
  }

  // EU (Non-Premium): All other Prod sites are hosted on prod-eu:
  return "eu";
};

/**
 * Convert a customer's sitename to the EnvId (e.g. au for prod-au) where its data should be hosted.
 *
 * @access private
 * @throws an error if called without a sitename parameter.
 *
 * @param {string} domain - e.g. cirrusrc in test-customer.cirrusrc.eu .
 * @param {string} tld - e.g. .eu in test-customer.cirrusrc.eu or null for localhost
 * @param {string} suffix - including "-", e.g. "-au" in api-au.cirrusplatform.com .
 * @param {string} imgIxSuffix - e.g. "" for eu/euprem, "-test" for acc/test/local.
 * @param {string} config - Cirrus configuration object.
 * @return {string} - null if OK, else error redirect "/500".
 */
function fillCirrusHostingConfig(domain, tld, suffix, imgIxSuffix, config) {
  if (domain === undefined || domain === null) {
    console.error("fillCirrusHostingConfig: domain missing or null");
    return "/500";
  }

  if (tld === undefined) {
    console.error("fillCirrusHostingConfig: tld missing (null is supported)");
    return "/500";
  }

  if (suffix === undefined || suffix === null) {
    console.error("fillCirrusHostingConfig: suffix missing or null");
    return "/500";
  }

  if (imgIxSuffix === undefined || imgIxSuffix === null) {
    console.error("fillCirrusHostingConfig: imgIxSuffix missing or null");
    return "/500";
  }

  if (config === undefined || config === null) {
    console.error("fillCirrusHostingConfig: config missing or null");
    return "/500";
  }

  config.Environment = domain;
  config.ImageHost = "https://cirrusplatform" + imgIxSuffix + ".imgix.net";

  if (domain === "localhost") {
    config.ApiHost = "http://localhost:9000";
    config.LtiHost = "http://localhost:9070";
    config.SSOApiHost = "http://localhost:9080";
    config.SamlApiHost = "http://localhost:9080"; // TODO: Remove all obsolete code... CR-21264
    config.SignalrHost = "http://localhost:9020";
    return null; // OK
  }

  const hostingSuffix = suffix + "." + domain + "." + tld;

  config.ApiHost = "https://api" + hostingSuffix;
  config.ImageHost = "https://cirrusplatform" + imgIxSuffix + ".imgix.net";
  config.LtiHost = "https://lti" + hostingSuffix;
  config.SSOApiHost = "https://sso" + hostingSuffix;
  config.SamlApiHost = "https://saml" + hostingSuffix; // TODO: Remove all obsolete code... CR-21264
  config.SignalrHost = "https://realtime" + hostingSuffix;

  return null; // OK
}

/**
 * Fill (hosting environment) config for an EnvID (e.g. au for prod-au)
 *
 * @access private
 *
 * @param {string} envId - EnvID e.g. au for prod-au .
 * @param {string} config - Cirrus configuration object.
 * @return {string} - null if OK, else error redirect ("/500") except for unsupported envId ("/404").
 */
function fillCirrusEnvConfig(envId, config) {
  if (envId === undefined || envId === null) {
    console.error("fillCirrusEnvConfig: envId missing or null");
    return "/500";
  }

  if (config === undefined || config === null) {
    console.error("fillCirrusEnvConfig: config missing or null");
    return "/500";
  }

  if (envId == "local") {
    return fillCirrusHostingConfig("localhost", null, "", "-test", config);
  }

  let domain = "cirrusplatform";
  let tld = "com";
  let suffix = "";
  let imgIxSuffix = "-test";

  /* eslint-disable padding-line-between-statements */
  switch (envId) {
    case "at":
      domain = "cirrusat";
      tld = "eu";
      break;
    case "eu0":
      domain = "cirrus0";
      tld = "eu";
      break;
    case "eu1":
      domain = "cirrus1";
      tld = "eu";
      break;
    case "eu2":
      domain = "cirrus2";
      tld = "eu";
      break;
    case "eu3":
      domain = "cirrus3";
      tld = "eu";
      break;
    case "eu4":
      domain = "cirrus4";
      tld = "eu";
      break;
    case "eu5":
      domain = "cirrus5";
      tld = "eu";
      break;
    case "next":
      domain = "cirrusnext";
      tld = "com";
      break;
    case "preview":
      domain = "cirruspreview";
      tld = "com";
      break;
    case "rc":
      domain = "cirrusrc";
      tld = "eu";
      break;

    // domain = "cirrusplatform", tld = "com"
    case "eu":
      imgIxSuffix = "";
      break;
    case "euprem":
      suffix = "-" + envId;
      imgIxSuffix = "-euprem";
      break;
    case "au":
    case "ca":
    case "sg":
      suffix = "-" + envId;
      imgIxSuffix = "-" + envId;
      break;
    case "eutemp":
      suffix = "-" + envId;
      imgIxSuffix = ""
      break;
    case "autemp":
      suffix = "-" + envId;
      imgIxSuffix = "-au"
      break;
    case "eupremtemp":
      suffix = "-" + envId;
      imgIxSuffix = "-euprem"
      break;
    case "catemp":
      suffix = "-" + envId;
      imgIxSuffix = "-ca"
      break;
    case "sgtemp":
      suffix = "-" + envId;
      imgIxSuffix = "-sg"
      break;
    default:
      console.error(
        "frontend-config.js: fillCirrusEnvConfig: unsupported envId: " + envId
      );
      return "/404";
  }
  /* eslint-enable padding-line-between-statements */

  return fillCirrusHostingConfig(domain, tld, suffix, imgIxSuffix, config);
}

/**
 * Parse full hostname (e.g. test-education.cirrusrc.eu) and set (hosting environment) config.
 *
 * @access private
 *
 * @param {string} hostname - e.g. ukdemo.cirrusplatform.com .
 * @param {string} config - Cirrus configuration object.
 * @return {string} - null if OK, else error redirect ("/500") except for unsupported envId ("/404").
 */
export const parseCirrusHostname = (hostname, config) => {
  if (process.env.NODE_ENV === "production" && location.hostname.split(".").length !== 3) {
    return "/site-entry"
  }

  if (hostname === undefined || hostname === null) {
    console.error("parseCirrusHostname: hostname missing or null");
    return "/500";
  }

  if (config === undefined || config === null) {
    console.error("parseCirrusHostname: config missing or null");
    return "/500";
  }

  const [sitename, domain, tld, ...hostnamerest] = hostname.split(".");

  // Localhost development:
  if (
    (sitename === "127" && domain === "0") ||
    sitename === "::1" ||
    (sitename === "localhost" && domain === undefined)
  ) {
    console.info("parseCirrusHostname: plain localhost (no suffix)");
    config.Environment = config.LocalhostDefaultEnv;
    config.Customer = config.LocalhostDefaultSite;

    const domain = config.LocalhostDefaultEnv;

    // domain === undefined can happen if Cirrus not initialized (missing config.LocalhostDefaultEnv)
    if (domain === undefined || domain === null || domain === "") {
      return null; // OK  - config.ApiHost should have been set.
    }

    // Support new envId (and old domain, e.g. "cirrus1")
    const localEnvId = sitenameSuffixToEnvId(
      "dummy-" + config.LocalhostDefaultEnv
    );

    if (localEnvId != null) {
      return fillCirrusEnvConfig(localEnvId, config);
    }

    return fillCirrusHostingConfig(domain, "eu", "", "-test", config);
  }

  // Some domain checks (for first line security defence)
  if (hostnamerest !== undefined && hostnamerest.length > 0) {
    console.error(
      "parseCirrusHostname: Unsupported Cirrus Fully qualified domain name! hostnamerest=" +
        (hostnamerest === undefined)
    );
    return "/404";
  }

  if (domain !== undefined && !domain.startsWith("cirrus")) {
    console.error(
      "parseCirrusHostname: Unsupported Cirrus domain name! domain=" + domain
    );
    return "/404";
  }

  // Override envId using sitename suffix (e.g. -au)
  // NOTE: -autemp for to test new hosting during migrations
  const overrideEnvId = sitenameSuffixToEnvId(sitename);

  if (overrideEnvId !== null) {
    // remove the envId from the sitename (akad-sg -> akad)
    const strippedSitename = sitename.slice(0, -(1 + overrideEnvId.length));

    config.Environment = "override-" + overrideEnvId;
    config.Customer =
      strippedSitename !== "localhost"
        ? strippedSitename
        : config.LocalhostDefaultSite;
    console.warn(
      "parseCirrusHostname: EnvId override in effect: envId: " +
        overrideEnvId +
        ", sitename: " +
        strippedSitename
    );

    return fillCirrusEnvConfig(overrideEnvId, config);
  }

  // Missing customer/sitename, e.g. cirrusplatform.com or cirrusrc.eu
  if (tld === undefined) {
    console.warn("parseCirrusHostname: Missing customer/sitename.");
    return "/site-entry";
  }

  // PRODUCTION: cirrusplatform.com:
  if (domain === "cirrusplatform" && tld === "com") {
    // only do sitename routing for production
    const envId = sitenameToEnvId(sitename);

    config.Environment = "cirrusplatform";
    config.Customer = sitename;
    return fillCirrusEnvConfig(envId, config);
  }

  // The different acc/test environments (w/o override)
  // eslint-disable-next-line prefer-destructuring
  config.Environment = domain.toLowerCase();
  config.Customer = sitename;
  return fillCirrusHostingConfig(domain, tld, "", "-test", config);
};

function init(window, config) {

  const error_redirect_url = parseCirrusHostname(
    window.location.hostname,
    config
  );

  if (error_redirect_url !== null) {
    window.location.href = error_redirect_url;
    return;
  }
}

const config = window.CirrusFrontendGlobalConfig;

init(window, config);

// TODO: check export not needed here?
export const getConfig = (key = window.subAppConfigKey) => {
  return { ...config, ...getSubAppConfig(key) };
};
